import {
  MolsonBox,
  MolsonCustomTooltip,
  MolsonDivider,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { formatPriceAndCurrency } from '@/utils/functions/data-utils'
import { useQuoteMarginColors } from '../hooks/useQuoteMarginColors'

interface BreakdownWrapperProps {
  buttons: ReactNode
  children: ReactNode
  hasDivider?: boolean
  isNonEditableQuote?: boolean
  maxMargin?: number
  marginType?: 'success' | 'error'
  marginPercentage?: string
  oldPrice?: number
  lifeCycleCost?: {
    change: boolean
    tooltipTitle: string
    tooltipParagraphs: string[]
  }
  targetPrice?: number
  top?: string
  totalPrice: number
  displayedOn?: 'ItemsTab' | 'TradeInTab'
}

export const BreakdownWrapper = ({
  buttons,
  children,
  hasDivider = true,
  isNonEditableQuote = false,
  maxMargin,
  marginPercentage,
  marginType = 'success',
  oldPrice,
  lifeCycleCost,
  targetPrice,
  top = '24px',
  totalPrice,
  displayedOn
}: BreakdownWrapperProps) => {
  const theme = molsonUseTheme()

  const { t } = useTranslation('', {
    keyPrefix: 'common'
  })

  const { textColor, borderColor, bgColor } = useQuoteMarginColors(marginType)

  const marginIconType = {
    error: (
      <MolsonIcon
        variant="ARROW_DROP_DOWN"
        iconColor="error"
        fontSize="small"
      />
    ),
    success: <MolsonIcon variant="CHECK" iconColor="success" fontSize="small" />
  }

  const iconType = marginIconType[marginType]
  const showTargetAndProposedPrices = displayedOn === 'ItemsTab'

  const renderMarginPercentage = () => (
    <>
      {showTargetAndProposedPrices && (
        <MolsonTypography
          data-testid="margin-percentage"
          bgcolor={bgColor}
          border={`1px solid ${borderColor}`}
          color={textColor}
          gap={0.25}
          sx={{
            alignItems: 'center',
            borderRadius: 1,
            display: 'flex',
            fontSize: 12,
            height: '25px',
            fontWeight: 600,
            px: 0.75,
            py: 0.25
          }}
        >
          {iconType ?? null}
          {marginPercentage}%
        </MolsonTypography>
      )}
    </>
  )

  return (
    <MolsonBox
      sx={{
        background: theme.palette.secondary[50],
        borderRadius: 4,
        position: 'sticky',
        top: top
      }}
      boxShadowType="default"
    >
      <MolsonBox
        sx={{
          padding: '24px'
        }}
      >
        <MolsonBox
          display="flex"
          alignItems="flex-start"
          flexDirection="column"
          justifyContent="space-between"
          gap={1}
        >
          <MolsonBox display="flex" gap={0.5}>
            <MolsonTypography
              sx={{
                fontSize: {
                  lg: '1.25rem',
                  xs: '1rem'
                }
              }}
              variant="h3"
            >
              {showTargetAndProposedPrices
                ? t('proposedPrice')
                : t('totalCost')}
            </MolsonTypography>
            {lifeCycleCost?.change && (
              <MolsonCustomTooltip
                config={{
                  title: lifeCycleCost.tooltipTitle,
                  paragraphsDescription: true,
                  paragraphs: lifeCycleCost.tooltipParagraphs
                }}
              />
            )}
          </MolsonBox>

          {oldPrice ? (
            <MolsonBox
              display="flex"
              alignItems="center"
              gap={oldPrice === totalPrice ? 1 : 0.5}
            >
              <MolsonTypography
                sx={{
                  color: theme.palette.secondary[500],
                  fontSize: {
                    lg: '1.5rem',
                    xs: '1.125rem'
                  },
                  fontWeight: 400,
                  textDecoration: 'line-through'
                }}
                data-testid="old-price"
                variant="h2"
              >
                {formatPriceAndCurrency(oldPrice)}
              </MolsonTypography>

              {(oldPrice > totalPrice || oldPrice < totalPrice) && (
                <MolsonIcon
                  iconColor={totalPrice > oldPrice ? 'success' : 'error'}
                  variant={
                    totalPrice > oldPrice ? 'ARROW_UPWARD' : 'ARROW_DOWNWARD'
                  }
                  data-testid="price-change-icon"
                />
              )}

              <MolsonTypography
                sx={{
                  fontSize: {
                    lg: '1.5rem',
                    xs: '1.125rem'
                  }
                }}
                data-testid="total-price"
                variant="h2"
              >
                {formatPriceAndCurrency(totalPrice)}
              </MolsonTypography>
            </MolsonBox>
          ) : (
            <MolsonBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <MolsonTypography data-testid="total-price" variant="h2">
                {formatPriceAndCurrency(totalPrice)}
              </MolsonTypography>
              {renderMarginPercentage()}
            </MolsonBox>
          )}
        </MolsonBox>

        {targetPrice !== undefined && maxMargin !== undefined && (
          <MolsonBox
            sx={{
              marginLeft: 'auto',
              width: 'fit-content'
            }}
          >
            <MolsonDivider
              sx={{
                borderColor: theme.palette.secondary[200],
                mb: 1,
                mt: 0.5
              }}
            />

            <MolsonTypography
              data-testid="target-price"
              sx={{ fontSize: '0.75rem', lineHeight: '16px' }}
            >
              {t(isNonEditableQuote ? 'sellingPrice' : 'targetPrice')}
              <span style={{ marginLeft: '4px' }}>
                {formatPriceAndCurrency(targetPrice)}
              </span>{' '}
              <span
                data-testid="max-margin"
                style={{ color: theme.palette.primary.success }}
              >
                {maxMargin}%
              </span>
            </MolsonTypography>
          </MolsonBox>
        )}

        <MolsonBox display="flex" alignItems="center" gap={2.5} mt={3}>
          {buttons}
        </MolsonBox>

        {hasDivider && <MolsonDivider data-testid="divider" sx={{ mt: 3 }} />}
      </MolsonBox>
      <MolsonBox sx={{ pb: 3 }}>{children}</MolsonBox>
    </MolsonBox>
  )
}
